/**
 * 📜 PAGE CGU - Conditions Générales d'Utilisation
 * Pour La Petite Épicerie (site principal)
 * Application créée et développée par Abdelghani BAALOULOU
 */

import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Shield, Heart, AlertTriangle, Users, Lock, Mail, Scale, Building2, Globe, FileText } from 'lucide-react';

const CGUPage = ({ onBack, isDarkMode = true }) => {
  const sections = [
    {
      title: "1. Éditeur et propriétaire de l'application",
      icon: <Building2 className="w-5 h-5" />,
      content: `Le présent site et l'application "La Petite Épicerie", accessible à l'adresse www.la-petite-epicerie.shop (ci-après « le Site » ou « l'Application »), ont été créés et sont la propriété exclusive de :

**Monsieur Abdelghani BAALOULOU**
Créateur, développeur et propriétaire de l'application

Adresse : 141 Rue Arthur Lamendin, 62980 Vermelles, France
Email : contact@la-petite-epicerie.shop
Téléphone : 07 80 64 89 76

**Directeur de la publication** : Monsieur Abdelghani BAALOULOU

L'application "La Petite Épicerie" est une création originale de Monsieur Abdelghani BAALOULOU, indépendante de toute autre entité commerciale.`
    },
    {
      title: "2. Hébergement",
      content: `Le Site est hébergé par :

**Emergent Labs, Inc.**
Plateforme d'hébergement cloud sécurisée
Site : https://emergent.sh`
    },
    {
      title: "3. Acceptation des conditions",
      content: `L'utilisation du Site implique l'acceptation pleine et entière des présentes Conditions Générales d'Utilisation (CGU).

Si vous n'acceptez pas ces conditions, veuillez ne pas utiliser le Site.

Ces conditions peuvent être modifiées à tout moment. Les utilisateurs sont invités à les consulter régulièrement.`
    },
    {
      title: "4. Services proposés",
      content: `L'application La Petite Épicerie propose :

• Un programme de fidélité digital innovant (cartes tampons virtuelles)
• Un système de commande en ligne
• Un service de livraison à domicile
• Des promotions et offres spéciales exclusives
• Des jeux et animations pour les clients fidèles
• Un espace client personnalisé et sécurisé

Le Site vise à offrir une expérience client moderne et engageante.`
    },
    {
      title: "5. Accès au service",
      icon: <Users className="w-5 h-5" />,
      content: `**Conditions d'accès** :
• Le Site est accessible 24h/24, 7j/7
• L'éditeur se réserve le droit de suspendre l'accès pour maintenance
• L'accès aux services nécessitant un compte requiert une inscription préalable

**Création de compte** :
• Les informations fournies doivent être exactes et à jour
• Vous êtes responsable de la confidentialité de vos identifiants
• Tout accès au Site via vos identifiants est réputé effectué par vous`
    },
    {
      title: "6. Programme de fidélité",
      content: `L'application propose un programme de fidélité avec cartes tampons virtuelles :

**Fonctionnement** :
• Chaque achat permet de cumuler des tampons
• Les tampons donnent droit à des avantages et réductions
• Les cartes sont personnelles et non transférables

**Conditions** :
• Les tampons expirent après 12 mois d'inactivité
• L'éditeur se réserve le droit de modifier le programme
• Toute fraude entraîne la suppression des avantages acquis`
    },
    {
      title: "7. Vente de boissons alcoolisées",
      icon: <AlertTriangle className="w-5 h-5" />,
      content: `**⚠️ L'ABUS D'ALCOOL EST DANGEREUX POUR LA SANTÉ**

Conformément à la législation française :
• La vente d'alcool est interdite aux mineurs de moins de 18 ans
• En passant commande, vous certifiez être majeur

**Contrôles** :
• Une vérification d'âge peut être demandée à la livraison
• En cas de doute, la livraison peut être refusée
• L'ivresse manifeste est un motif de refus de vente`
    },
    {
      title: "8. Livraison",
      content: `**Zones de livraison** :
Le service de livraison est disponible dans un rayon défini autour de Vermelles.

**Horaires** :
Les créneaux de livraison sont affichés lors de la commande.

**Responsabilité** :
• L'éditeur s'engage à assurer un service de qualité
• En cas de retard exceptionnel, vous serez prévenu
• La responsabilité est limitée au montant de la commande`
    },
    {
      title: "9. Prix et paiement",
      content: `**Prix** :
• Les prix sont indiqués en euros TTC
• Ils sont susceptibles de modifications sans préavis
• Le prix applicable est celui affiché au moment de la commande

**Paiement** :
• Le paiement s'effectue au comptant lors de la commande ou à la livraison
• Moyens de paiement acceptés : CB, espèces, autres moyens indiqués
• Toute commande impayée sera annulée`
    },
    {
      title: "10. Propriété intellectuelle",
      icon: <Scale className="w-5 h-5" />,
      content: `L'ensemble des éléments du Site et de l'Application (textes, images, logos, design, code source, fonctionnalités) est la propriété exclusive de Monsieur Abdelghani BAALOULOU.

**Interdictions** :
• Toute reproduction, représentation ou utilisation non autorisée est strictement interdite
• Les marques "La Petite Épicerie" et les logos associés sont protégés
• Toute utilisation frauduleuse est passible de poursuites judiciaires

© 2026 Abdelghani BAALOULOU - Tous droits réservés`
    },
    {
      title: "11. Responsabilité",
      content: `**Limitation de responsabilité** :
• L'éditeur ne saurait être tenu responsable des dommages indirects
• Les informations du Site sont fournies à titre indicatif
• L'éditeur ne garantit pas la disponibilité permanente du Site

**Force majeure** :
La responsabilité de l'éditeur ne saurait être engagée en cas de force majeure.`
    },
    {
      title: "12. Protection des données personnelles",
      icon: <Lock className="w-5 h-5" />,
      content: `Vos données personnelles sont traitées conformément au Règlement (UE) 2016/679 (RGPD) et à la loi n°78-17 du 6 janvier 1978.

**Vos droits** :
• Droit d'accès à vos données
• Droit de rectification
• Droit à l'effacement
• Droit à la portabilité
• Droit d'opposition

**Responsable du traitement** : Monsieur Abdelghani BAALOULOU
**Contact DPO** : privacy@la-petite-epicerie.shop

Vous pouvez également adresser une réclamation à la CNIL : www.cnil.fr`
    },
    {
      title: "13. Droit applicable et juridiction",
      icon: <FileText className="w-5 h-5" />,
      content: `Les présentes CGU sont régies par le droit français.

**En cas de litige** :
• Une solution amiable sera recherchée en priorité
• À défaut, les tribunaux français du ressort de la Cour d'Appel de Douai seront compétents

**Médiation** :
Conformément à l'article L.612-1 du Code de la consommation, vous pouvez recourir gratuitement à un médiateur de la consommation.`
    },
    {
      title: "14. Contact",
      icon: <Mail className="w-5 h-5" />,
      content: `Pour toute question concernant ces CGU ou l'Application :

**Monsieur Abdelghani BAALOULOU**
Créateur et propriétaire de l'application La Petite Épicerie

Adresse : 141 Rue Arthur Lamendin, 62980 Vermelles, France
Email : contact@la-petite-epicerie.shop
Téléphone : 07 80 64 89 76

Dernière mise à jour : Février 2026`
    }
  ];

  return (
    <div className={`min-h-screen overflow-x-hidden overflow-y-auto ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      {/* Header */}
      <div className={`sticky top-0 z-10 ${isDarkMode ? 'bg-gray-900/95' : 'bg-white/95'} backdrop-blur-sm border-b ${isDarkMode ? 'border-gray-800' : 'border-gray-200'}`}>
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          {onBack && (
            <button onClick={onBack} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </button>
          )}
          <div className="flex items-center gap-2">
            <Shield className="w-6 h-6 text-cyan-400" />
            <h1 className="text-xl font-bold">Conditions Générales d'Utilisation</h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`p-6 rounded-2xl mb-8 ${isDarkMode ? 'bg-gradient-to-r from-cyan-900/30 to-purple-900/30 border border-cyan-500/30' : 'bg-cyan-50 border border-cyan-200'}`}
        >
          <div className="flex items-center gap-3 mb-3">
            <span className="text-4xl">🛒</span>
            <div>
              <h2 className="font-bold text-lg">La Petite Épicerie</h2>
              <p className={`text-sm ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>
                Application créée par Abdelghani BAALOULOU
              </p>
            </div>
          </div>
          <p className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
            Bienvenue sur La Petite Épicerie ! Ces conditions générales d'utilisation régissent votre utilisation 
            de notre site et de nos services. Merci de les lire attentivement avant toute utilisation.
          </p>
        </motion.div>

        {sections.map((section, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.03 }}
            className={`mb-6 p-6 rounded-xl ${isDarkMode ? 'bg-gray-800/50 hover:bg-gray-800/70' : 'bg-white hover:bg-gray-50'} border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} transition-colors`}
          >
            <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
              {section.icon && <span className="text-cyan-400">{section.icon}</span>}
              {section.title}
            </h3>
            <div className={`whitespace-pre-line leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              {section.content}
            </div>
          </motion.div>
        ))}

        {/* Footer */}
        <div className={`text-center py-8 border-t ${isDarkMode ? 'border-gray-800 text-gray-500' : 'border-gray-200 text-gray-400'}`}>
          <p className="text-sm">© 2026 La Petite Épicerie - Créé par Abdelghani BAALOULOU</p>
          <p className="text-xs mt-1">Tous droits réservés</p>
        </div>
      </div>
    </div>
  );
};

export default CGUPage;
